import dayjs from 'dayjs'
import { Participant, Team } from '../types'
import { TEAM_CONFIG, DATE_TIME } from '../constants'
import { ACTIVITY_MIN_GAP_MINUTES } from '../config'

/**
 * Generate initials from a person's name
 */
export const generateInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2)
}

/**
 * Generate a unique team name based on existing teams
 */
export const generateTeamName = (existingTeams: Team[]): string => {
  const usedNames = existingTeams.map((team) => team.name.replace('Team ', ''))
  const availableName = TEAM_CONFIG.TEAM_NAMES.find((name) => !usedNames.includes(name))
  return availableName ? `Team ${availableName}` : `Team ${existingTeams.length + 1}`
}

/**
 * Generate a unique ID for participants or activities
 */
export const generateUniqueId = (prefix: string): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Calculate end time based on start time and duration
 */
export const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const [hours, minutes] = startTime.split(':').map(Number)
  const startDate = new Date()
  startDate.setHours(hours, minutes, 0, 0)

  const endDate = new Date(startDate.getTime() + durationMinutes * 60000)

  const endHours = endDate.getHours().toString().padStart(2, '0')
  const endMinutes = endDate.getMinutes().toString().padStart(2, '0')

  return `${endHours}:${endMinutes}`
}

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', DATE_TIME.DATE_FORMAT_OPTIONS)
}

/**
 * Format time for display (24h to 12h)
 */
export const formatTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(':')
  const hour24 = parseInt(hours)
  const hour12 = DATE_TIME.TIME_FORMAT_24_TO_12[hour24 as keyof typeof DATE_TIME.TIME_FORMAT_24_TO_12] || hour24
  const ampm = hour24 >= 12 ? 'PM' : 'AM'
  return `${hour12}:${minutes} ${ampm}`
}

/**
 * Get the next available scheduling slot based on the previous activity.
 *
 * - If previous activity is fully scheduled, returns its end date/time + ACTIVITY_MIN_GAP_MINUTES.
 * - Otherwise returns now + ACTIVITY_MIN_GAP_MINUTES.
 *
 * @param previousActivity - The preceding scheduled activity (optional).
 * @returns Object with `date` (YYYY-MM-DD) and `time` (HH:mm).
 */
export const getNextAvailableSlot = (previousActivity?: {
  scheduledDate?: string
  endTime?: string
}): { date: string; time: string } => {
  if (previousActivity?.scheduledDate && previousActivity?.endTime) {
    const next = dayjs(`${previousActivity.scheduledDate}T${previousActivity.endTime}`).add(
      ACTIVITY_MIN_GAP_MINUTES,
      'minute'
    )
    return { date: next.format('YYYY-MM-DD'), time: next.format('HH:mm') }
  }
  const next = dayjs().add(ACTIVITY_MIN_GAP_MINUTES, 'minute')
  return { date: next.format('YYYY-MM-DD'), time: next.format('HH:mm') }
}

/**
 * Get unassigned participants count
 */
export const getUnassignedParticipantsCount = (participants: Participant[], teams: Team[]): number => {
  const teamParticipantIds = new Set(teams.flatMap((team) => team.participants.map((p) => p.id)))
  return participants.filter((p) => !teamParticipantIds.has(p.id)).length
}

/**
 * Check if teams have insufficient members
 */
export const hasTeamsWithInsufficientMembers = (teams: Team[]): boolean => {
  return teams.some((team) => team.participants.length < TEAM_CONFIG.MIN_PARTICIPANTS_FOR_VALIDATION)
}

// Email validation is now handled by the centralized helper

/**
 * Auto-assign participants to teams
 */
export const autoAssignParticipants = (
  participants: Participant[],
  teams: Team[]
): { participants: Participant[]; teams: Team[] } => {
  const unassignedParticipantIds = new Set(
    participants.filter((p) => !teams.some((team) => team.participants.some((tp) => tp.id === p.id))).map((p) => p.id)
  )

  const updatedTeams = teams.map((team) => ({ ...team }))
  const updatedParticipants = [...participants]

  let teamIndex = 0
  for (const participantId of unassignedParticipantIds) {
    const participant = updatedParticipants.find((p) => p.id === participantId)
    if (!participant) continue

    // Find team with available space
    let assigned = false
    let attempts = 0
    while (!assigned && attempts < teams.length) {
      const currentTeam = updatedTeams[teamIndex]
      if (currentTeam.participants.length < currentTeam.maxParticipants) {
        currentTeam.participants.push(participant)
        assigned = true
      }
      teamIndex = (teamIndex + 1) % teams.length
      attempts++
    }
  }

  return { participants: updatedParticipants, teams: updatedTeams }
}
